import { Button, Space } from 'antd';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { NotificationContext } from '@client/context/NotificationContext';
import { useError } from '@client/hooks/useError';
import { useFeatureQueries } from '@client/queries/useFeatureQueries';
import { getSubscriptionsByFeature } from '@client/services/subscription.service';
import { useLoading } from '@client/store/loading.store';
import { ERoutePaths } from '@client/types';
import {
  EErrorType,
  type IFeature,
  type TErrorData,
  EStatus,
} from '@common/types';

export const useFeatures = () => {
  const { useUserFeatures, useDeactivateFeature, useToggleActive } =
    useFeatureQueries();
  const { setError } = useError();
  const navigate = useNavigate();
  const { notificationApi } = useContext(NotificationContext);
  const { t } = useTranslation();

  // Query for user features
  const { data, refetch, isLoading, isFetching, isError, error } =
    useUserFeatures();

  const { setLoading } = useLoading(false);

  // Mutation for deactivating a feature
  const deactivateMutation = useDeactivateFeature();
  const activeMutation = useToggleActive();

  const onDeactivate = useCallback(
    async (entity: IFeature): Promise<void> => {
      try {
        setLoading(true);
        await deactivateMutation.mutateAsync(entity.id as string);
      } catch (error) {
        setError({
          data: error as TErrorData,
          description: EErrorType.FAILED_DEACTIVATE,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [deactivateMutation, setError, setLoading],
  );

  const onBulkToggleActive = useCallback(
    async (selectedFeatures: IFeature[], activate: boolean): Promise<void> => {
      try {
        setLoading(true);

        const featuresToProcess = selectedFeatures.filter(
          (f) =>
            (activate && f.status !== EStatus.ACTIVE) ||
            (!activate && f.status === EStatus.ACTIVE),
        );

        if (featuresToProcess.length === 0) return;

        if (!activate) {
          // If deactivating, we check subscription assignments for each feature
          const blocked: {
            feature: IFeature;
            subscriptionTitle: string;
            subscriptionId: string;
          }[] = [];
          const allowed: IFeature[] = [];

          await Promise.all(
            featuresToProcess.map(async (feature) => {
              const subscriptions = await getSubscriptionsByFeature(
                feature.id as string,
              );
              if (subscriptions.length > 0) {
                blocked.push({
                  feature,
                  subscriptionTitle: subscriptions[0].title || '',
                  subscriptionId: subscriptions[0].id as string,
                });
              } else {
                allowed.push(feature);
              }
            }),
          );

          if (blocked.length > 0) {
            blocked.forEach(
              ({ feature, subscriptionTitle, subscriptionId }) => {
                const key = `deactivate-blocked-${feature.id}`;
                notificationApi?.warning({
                  key,
                  title: t('feature.msg.deactivateBlocked'),
                  description: t('feature.msg.deactivateBlockedDescription', {
                    subscriptionName: subscriptionTitle,
                  }),
                  actions: (
                    <Space>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          notificationApi.destroy(key);
                          navigate(
                            ERoutePaths.SUBSCRIPTION_EDIT.replace(
                              ':subscriptionId',
                              subscriptionId,
                            ),
                          );
                        }}
                      >
                        {t('feature.actions.navigateToSubscription')}
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => notificationApi.destroy(key)}
                      >
                        {t('actions.close')}
                      </Button>
                    </Space>
                  ),
                });
              },
            );
          }

          if (allowed.length > 0) {
            await Promise.all(
              allowed.map((feature) => activeMutation.mutateAsync(feature)),
            );
          }
        } else {
          // If activating, just activate all allowed features
          await Promise.all(
            featuresToProcess.map((feature) =>
              activeMutation.mutateAsync(feature),
            ),
          );
        }
      } catch (error) {
        setError({
          data: error as TErrorData,
          description: activate
            ? EErrorType.FAILED_ACTIVATE
            : EErrorType.FAILED_DEACTIVATE,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [activeMutation, setError, setLoading, notificationApi, navigate, t],
  );

  const onEdit = useCallback(
    (id: string): void => {
      navigate(`/features/${id}`);
    },
    [navigate],
  );

  return {
    loading: isLoading || isFetching,
    isError,
    error,
    entities: data?.features || [],
    total: data?.total || 0,
    onDeactivate,
    onEdit,
    onBulkToggleActive,
    refetch,
  };
};
